import { useMemo } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Row, Col } from 'reactstrap';
import ModalWithFinalForm from '../../../../modals/ModalWithFinalForm';
import FormFieldTextOrNumber from '../../../../formFields/FormFieldTextOrNumber';
import FormFieldWithDropdown from '../../../../formFields/FormFieldWithDropdown';
import { PATCH_RAW_HTML_TABLE_CELL_STYLE_COMMAND, PATCH_RAW_HTML_TABLE_STYLE_COMMAND } from '../../../nodes/rawHtmlTable/commands';
import { formatCssLength, formatTwoValueCssLength, parseCssLength, parseTwoValueCssLength } from '../../../nodes/rawHtmlTable/cssLength';
import { RawHtmlTableSelectionContext } from '../../../nodes/rawHtmlTable/selection';
import { $isRawHtmlTableNode } from '../../../nodes/rawHtmlTable/RawHtmlTableNode';
import { $isRawHtmlTableCellNode } from '../../../nodes/rawHtmlTable/RawHtmlTableCellNode';
import { $getNodeByKey } from 'lexical';

type TablePropertiesModalProperties =
{
    isOpen: boolean
    setIsOpen: (value: boolean) => void
    tableSelectionContext: RawHtmlTableSelectionContext | null
}

type TablePropertiesForm =
{
    widthValue?: string;
    widthUnit?: string;

    borderCollapse?: 'collapse' | 'separate' | '';

    borderSpacingValue?: string;
    borderSpacingUnit?: string;

    paddingVerticalValue?: string;
    paddingVerticalUnit?: string;
    paddingHorizontalValue?: string;
    paddingHorizontalUnit?: string;

    borderWidthValue?: string;
    borderWidthUnit?: string;
    borderStyle?: string;
    borderColor?: string;
};

const unitOptions =
[
    { id: '', label: 'brak' },
    { id: 'px', label: 'px' },
    { id: '%', label: '%' },
    { id: 'em', label: 'em' },
    { id: 'rem', label: 'rem' },
];

const borderStyleOptions =
[
    { id: '', label: 'brak' },
    { id: 'solid', label: 'solid' },
    { id: 'dashed', label: 'dashed' },
    { id: 'dotted', label: 'dotted' },
    { id: 'none', label: 'none' },
];

const borderCollapseOptions =
[
    { id: '', label: 'domyślnie' },
    { id: 'collapse', label: 'collapse' },
    { id: 'separate', label: 'separate' },
];

function parseBorderCss(border?: string): { borderWidthValue?: string; borderWidthUnit?: string; borderStyle?: string; borderColor?: string; }
{
    const value = border?.trim();

    if (!value) { return {}; }
    if (value === 'none') { return { borderStyle: 'none' }; }

    const parts = value.split(/\s+/);
    const widthPart = parts.find(part => /^\d+(\.\d+)?(px|em|rem|%)?$/.test(part));
    const stylePart = parts.find(part => ['solid', 'dashed', 'dotted', 'double', 'none'].includes(part));
    const colorPart = parts.find(part => part !== widthPart && part !== stylePart);

    const width = parseCssLength(widthPart);

    return {
        borderWidthValue: width.value,
        borderWidthUnit: width.unit || 'px',
        borderStyle: stylePart || '',
        borderColor: colorPart || '',
    };
}

function createBorderCss(form: TablePropertiesForm): string | null
{
    const width = formatCssLength(form.borderWidthValue, form.borderWidthUnit as any);
    const style = form.borderStyle?.trim();
    const color = form.borderColor?.trim();

    if (style === 'none') { return 'none'; }
    if (!width) { return null; }

    return [width, style || 'solid', color || '#bbb'].filter(Boolean).join(' ');
}

function createEmptyInitialValues(): TablePropertiesForm
{
    return {
        widthUnit: '%',
        borderCollapse: '',
        borderSpacingUnit: 'px',
        paddingVerticalUnit: 'px',
        paddingHorizontalUnit: 'px',
        borderWidthUnit: 'px',
        borderStyle: '',
        borderColor: '',
    };
}

function createInitialValuesFromTableState
(
    editor: any,
    tableSelectionContext: RawHtmlTableSelectionContext | null
)
: TablePropertiesForm
{
    const result = createEmptyInitialValues();

    if (!tableSelectionContext) {
        return result;
    }

    editor.getEditorState().read(() => {
        const tableNode = $getNodeByKey(tableSelectionContext.tableKey);

        if ($isRawHtmlTableNode(tableNode)) {
            const tableStyle = tableNode.getRawHtmlTableStyle();

            const width = parseCssLength(tableStyle?.width);
            const borderSpacing = parseCssLength(tableStyle?.borderSpacing);

            result.widthValue = width.value;
            result.widthUnit = width.unit || result.widthUnit;

            result.borderCollapse = tableStyle?.borderCollapse ?? '';

            result.borderSpacingValue = borderSpacing.value;
            result.borderSpacingUnit = borderSpacing.unit || result.borderSpacingUnit;
        }

        const firstCellKey = tableSelectionContext.cellKeys[0];

        if (firstCellKey) {
            const cellNode = $getNodeByKey(firstCellKey);

            if ($isRawHtmlTableCellNode(cellNode)) {
                const cellStyle = cellNode.getRawHtmlTableCellStyle();

                const padding = parseTwoValueCssLength(cellStyle?.padding);
                const border = parseBorderCss(cellStyle?.border);

                result.paddingVerticalValue = padding.first.value;
                result.paddingVerticalUnit = padding.first.unit || result.paddingVerticalUnit;

                result.paddingHorizontalValue = padding.second.value;
                result.paddingHorizontalUnit = padding.second.unit || result.paddingHorizontalUnit;

                result.borderWidthValue = border.borderWidthValue;
                result.borderWidthUnit = border.borderWidthUnit || result.borderWidthUnit;
                result.borderStyle = border.borderStyle || '';
                result.borderColor = border.borderColor || '';
            }
        }
    });

    return result;
}

export default function TablePropertiesModal({ isOpen, setIsOpen, tableSelectionContext }: TablePropertiesModalProperties)
{
    const [editor] = useLexicalComposerContext();

    const initialValues = useMemo<TablePropertiesForm>
    (
        () => createInitialValuesFromTableState(editor, tableSelectionContext),
        [editor, tableSelectionContext]
    );

    const handleConfirm = (form: TablePropertiesForm) =>
    {
        const tablePatch: any = {};
        const cellPatch: any = {};
        const width = formatCssLength(form.widthValue, form.widthUnit as any);
        const initialWidth = formatCssLength(initialValues.widthValue, initialValues.widthUnit as any);

        if (width !== initialWidth) { tablePatch.width = width ?? null; }

        const borderSpacing = formatCssLength(form.borderSpacingValue, form.borderSpacingUnit as any);
        const initialBorderSpacing = formatCssLength(initialValues.borderSpacingValue, initialValues.borderSpacingUnit as any);

        if (borderSpacing !== initialBorderSpacing) { tablePatch.borderSpacing = borderSpacing ?? null; }

        if ((form.borderCollapse || '') !== (initialValues.borderCollapse || ''))
        {
            tablePatch.borderCollapse = form.borderCollapse || null;
        }

        const padding = formatTwoValueCssLength
        (
            form.paddingVerticalValue,
            form.paddingVerticalUnit as any,
            form.paddingHorizontalValue,
            form.paddingHorizontalUnit as any
        );

        const initialPadding = formatTwoValueCssLength
        (
            initialValues.paddingVerticalValue,
            initialValues.paddingVerticalUnit as any,
            initialValues.paddingHorizontalValue,
            initialValues.paddingHorizontalUnit as any
        );

        if (padding !== initialPadding) { cellPatch.padding = padding ?? null; }

        const border = createBorderCss(form);
        const initialBorder = createBorderCss(initialValues);

        if (border !== initialBorder) { cellPatch.border = border ?? null; }

        if (Object.keys(tablePatch).length > 0)
        {
            const handled = editor.dispatchCommand(PATCH_RAW_HTML_TABLE_STYLE_COMMAND, {
                tableKey: tableSelectionContext?.tableKey,
                patch: tablePatch,
            });
            console.log('PATCH_RAW_HTML_TABLE_STYLE_COMMAND handled', handled, tablePatch, tableSelectionContext);
        }

        if (Object.keys(cellPatch).length > 0)
        {
            const handled = editor.dispatchCommand(PATCH_RAW_HTML_TABLE_CELL_STYLE_COMMAND, {
                tableKey: tableSelectionContext?.tableKey,
                cellKeys: tableSelectionContext?.cellKeys,
                patch: cellPatch,
            });
            console.log('PATCH_RAW_HTML_TABLE_CELL_STYLE_COMMAND handled', handled, cellPatch, tableSelectionContext);
        }

        setIsOpen(false);
    };

    return (
        <ModalWithFinalForm
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            size="xl"
            initialValues={initialValues}
            onConfirm={handleConfirm}
            header="Właściwości tabeli"
            buttons={{
                confirm: { color: 'success', label: 'Zastosuj' },
                cancel: { color: 'danger', label: 'Anuluj' },
            }}
            modalBody={
                <>
                    <h6>Tabela</h6>

                    <Row className="my-2">
                        <Col md={6}>
                            <FormFieldTextOrNumber
                                name="widthValue"
                                label="Szerokość"
                                className="w-100"
                            />
                        </Col>
                        <Col md={6}>
                            <FormFieldWithDropdown
                                name="widthUnit"
                                label="Jednostka"
                                options={unitOptions}
                            />
                        </Col>
                    </Row>

                    <Row className="my-2">
                        <Col md={6}>
                            <FormFieldWithDropdown
                                name="borderCollapse"
                                label="Border collapse"
                                options={borderCollapseOptions}
                            />
                        </Col>
                    </Row>

                    <Row className="my-2">
                        <Col md={6}>
                            <FormFieldTextOrNumber
                                name="borderSpacingValue"
                                label="Border spacing"
                                className="w-100"
                            />
                        </Col>
                        <Col md={6}>
                            <FormFieldWithDropdown
                                name="borderSpacingUnit"
                                label="Jedn."
                                options={unitOptions}
                            />
                        </Col>
                    </Row>

                    <hr />

                    <h6>Komórki</h6>

                    <Row className="my-2">
                        <Col md={6}>
                            <FormFieldTextOrNumber
                                name="paddingVerticalValue"
                                label="Padding pionowy"
                                className="w-100"
                            />
                        </Col>
                        <Col md={6}>
                            <FormFieldWithDropdown
                                name="paddingVerticalUnit"
                                label="Jedn."
                                options={unitOptions}
                            />
                        </Col>

                        <Col md={6}>
                            <FormFieldTextOrNumber
                                name="paddingHorizontalValue"
                                label="Padding poziomy"
                                className="w-100"
                            />
                        </Col>
                        <Col md={6}>
                            <FormFieldWithDropdown
                                name="paddingHorizontalUnit"
                                label="Jedn."
                                options={unitOptions}
                            />
                        </Col>
                    </Row>

                    <Row className="my-2">
                        <Col md={6}>
                            <FormFieldTextOrNumber
                                name="borderWidthValue"
                                label="Szerokość obramowania"
                                className="w-100"
                            />
                        </Col>
                        <Col md={6}>
                            <FormFieldWithDropdown
                                name="borderWidthUnit"
                                label="Jednostka"
                                options={unitOptions}
                            />
                        </Col>
                    </Row>

                    <Row className="my-2">
                        <Col md={6}>
                            <FormFieldWithDropdown
                                name="borderStyle"
                                label="Styl obramowania"
                                options={borderStyleOptions}
                            />
                        </Col>
                        <Col md={6}>
                            <FormFieldTextOrNumber
                                name="borderColor"
                                label="Kolor obramowania"
                                className="w-100"
                            />
                        </Col>
                    </Row>
                </>
            }
        />
    );
}